/**
 * Check if a value is an empty placeholder that should be sorted to the end.
 * Handles null, undefined, empty strings, and common placeholder values like "-" and "--".
 */
export function isEmptyPlaceholder(value: unknown): boolean {
  if (value == null) return true;
  const str = String(value).trim();
  return str === "" || str === "-" || str === "--";
}

/**
 * Comparator function that pushes empty placeholder values to the end of sorted arrays.
 * Returns comparison result that can be used directly in Array.sort().
 *
 * @param aValue - First value to compare
 * @param bValue - Second value to compare
 * @returns 0 if both empty, 1 if a is empty (push to end), -1 if b is empty (push to end), undefined if both have values
 *
 * @example
 * ```typescript
 * array.sort((a, b) => {
 *   const emptySort = sortEmptyLast(a.date, b.date);
 *   if (emptySort !== undefined) return emptySort;
 *
 *   // Both have values, do actual comparison
 *   return dayjs(b.date).diff(dayjs(a.date));
 * });
 * ```
 */
export function sortEmptyLast(aValue: unknown, bValue: unknown): number | undefined {
  const aIsEmpty = isEmptyPlaceholder(aValue);
  const bIsEmpty = isEmptyPlaceholder(bValue);

  if (aIsEmpty && bIsEmpty) return 0;
  if (aIsEmpty) return 1;
  if (bIsEmpty) return -1;

  return undefined; // Both have values, caller should do actual comparison
}
